import React, { useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { Shield, AlertTriangle } from 'lucide-react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

interface AdminProtectedRouteProps {
  children: React.ReactNode
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const { user, profile, loading, isAdmin, isEditor, refreshProfile } = useAuth()
  const navigate = useNavigate()

  const hasAccess = isAdmin || isEditor

  // Force refresh profile when component mounts
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user && !hasAccess) {
        try {
          const freshProfile = await refreshProfile()

          if (freshProfile && !freshProfile.is_admin && !freshProfile.is_editor) {
            toast.error('Você precisa ter permissões de administrador ou editor para acessar esta área')
          }
        } catch (error) {
          toast.error('Erro ao verificar permissões')
        }
      }
    }

    checkAdminStatus()
  }, [user, hasAccess, refreshProfile])

  // Show loading screen while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen bg-cream-50 dark:bg-ink-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-terra-500 dark:bg-terra-700 rounded-caderno-md flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div className="w-8 h-8 border-4 border-terra-500 dark:border-terra-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-ink-500 dark:text-ink-300">Verificando permissões...</p>
        </div>
      </div>
    )
  }

  // Redirect to login if no user
  if (!user) {
    return <Navigate to="/" replace />
  }

  // Show access denied if not admin or editor
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-cream-50 dark:bg-ink-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface dark:bg-ink-700 rounded-caderno-md border border-cream-400 dark:border-ink-500 p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-blood-100 dark:bg-blood-500/30 dark:bg-blood-500/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-blood-500 dark:text-blood-500" />
          </div>
          
          <h2 className="text-xl font-bold text-ink-900 dark:text-cream-50 mb-2">
            Acesso Negado
          </h2>
          
          <p className="text-ink-500 dark:text-ink-300 mb-6">
            Você não tem permissão para acessar o painel de administração.
            Verifique se você marcou a opção "Administrador" nas configurações do seu perfil.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.history.back()}
              className="bg-cream-200 hover:bg-cream-200 dark:bg-ink-700 dark:hover:bg-ink-700 text-ink-700 dark:text-cream-100 px-6 py-2 rounded-caderno-sm transition-colors"
            >
              Voltar
            </button>
            
            <button
              onClick={() => navigate('/app/settings')}
              className="bg-terra-500 hover:bg-terra-700 dark:bg-terra-700 dark:hover:bg-terra-700 text-white px-6 py-2 rounded-caderno-sm transition-colors"
            >
              Ir para Configurações
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  // Show admin interface if user has access
  return <>{children}</>
}

export default AdminProtectedRoute